import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useMechanicAuth } from '../MechanicAuthContext';
import axios from 'axios';
import {
  ArrowLeft, Save, Ban, Clock, Wrench, Package, User, Truck, Plus, Minus, X,
  CheckCircle2, Printer, ChevronDown, ChevronUp, Gauge, FileText,
  Disc, CircleDot, Settings, Search as SearchIcon, History, Camera, Image as ImageIcon,
  Play, Square, Timer, ScanLine
} from 'lucide-react';
import { toast } from 'sonner';
import VehicleHistoryModal from '../../components/VehicleHistoryModal';
import { CancelWorkOrderDialog, CancelledBanner } from '../components/CancelWorkOrderDialog';
import { WorkOrderClientPicker } from '../components/WorkOrderClientPicker';
import { OrderPartsList, PartSourceBadge } from '../components/OrderPartsList';
import { InventoryPartPicker } from '../components/inventory/InventoryPartPicker';
import AxleDiagram from '../../components/AxleDiagram';
import SaaqInspectionClassic from '../../components/SaaqInspectionClassic';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = { open: 'Ouvert', in_progress: 'En cours', completed: 'Complete', cancelled: 'ANNULÉ' };
const chronoHours = (t) => {
  if (!t.start_time || !t.end_time) return 0;
  const h = (new Date(t.end_time) - new Date(t.start_time)) / 3600000;
  return h > 0 ? Math.round(h * 100) / 100 : 0;
};
const STATUS_COLORS = { open: 'bg-blue-500 text-white', in_progress: 'bg-amber-500 text-black', completed: 'bg-emerald-500 text-white', cancelled: 'bg-red-600 text-white' };

const TASK_TYPES = [
  { key: 'diagnostic', label: 'Diagnostique', icon: SearchIcon, color: 'text-amber-400 bg-amber-500/15' },
  { key: 'reparation', label: 'Reparation', icon: Wrench, color: 'text-orange-400 bg-orange-500/15' },
  { key: 'entretien', label: 'Entretien', icon: Settings, color: 'text-blue-400 bg-blue-500/15' },
  { key: 'inspection', label: 'Inspection', icon: FileText, color: 'text-emerald-400 bg-emerald-500/15' },
  { key: 'mesures', label: 'Mesures freins/pneus', icon: Gauge, color: 'text-purple-400 bg-purple-500/15' },
];

// Legacy types for backward compatibility display
const LEGACY_TASK_TYPES = {
  mesures_pneus: { label: 'Pneus (ancien)', icon: CircleDot, color: 'text-purple-400 bg-purple-500/15' },
  mesures_freins: { label: 'Freins (ancien)', icon: Disc, color: 'text-red-400 bg-red-500/15' },
};

const CONDITIONS = ['Bon', 'Acceptable', 'A surveiller', 'A remplacer'];

const inputCls = "w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:border-amber-500/50 focus:outline-none disabled:opacity-50";
const selectCls = "w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-white focus:border-amber-500/50 focus:outline-none disabled:opacity-50";

const Field = ({ label, value, onChange, type = 'text', placeholder = '', rows, disabled, className = '' }) => (
  <div className={className}>
    <label className="block text-xs text-slate-400 mb-1">{label}</label>
    {rows ? (
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows} disabled={disabled} className={inputCls} />
    ) : (
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} className={inputCls} />
    )}
  </div>
);

const Section = ({ title, icon: Icon, children, defaultOpen = true, badge }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 p-4 text-left" data-testid={`section-${title.toLowerCase().replace(/\s/g, '-')}`}>
        <Icon size={18} className="text-amber-400" />
        <span className="font-semibold text-white text-sm flex-1">{title}</span>
        {badge && <span className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">{badge}</span>}
        {open ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  );
};

const nowStr = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}T${String(n.getHours()).padStart(2,'0')}:${String(n.getMinutes()).padStart(2,'0')}`;
};

const generateId = () => 'task_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 9);

const calcDuration = (s, e) => {
  if (!s || !e) return null;
  const ms = new Date(e) - new Date(s);
  if (ms <= 0) return null;
  const m = Math.floor(ms / 60000);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h${(m % 60) > 0 ? String(m % 60).padStart(2, '0') : ''}` : `${m}min`;
};

// Live stopwatch hook
const useStopwatch = (startTime) => {
  const [elapsed, setElapsed] = useState('');
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!startTime || !running) { setElapsed(''); return; }
    const tick = () => {
      const ms = Date.now() - new Date(startTime.replace('T', ' ')).getTime();
      if (ms < 0) { setElapsed('00:00:00'); return; }
      const s = Math.floor(ms / 1000);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      setElapsed(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startTime, running]);

  return { elapsed, running, setRunning };
};

// --- Task Components ---

const SaaqInspectionTask = ({ data, onChange, disabled, token, vehicleType, vehicleInfo, inspectorName, inspectionDate }) => {
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);

  const inspectionData = data || {};
  const statuses = inspectionData.statuses || {};
  const meas = inspectionData.measurements || {};
  const notes = inspectionData.notes || '';

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const vt = vehicleType || 'truck';
        const res = await axios.get(`${API_URL}/api/mechanic/saaq/categories?vehicle_type=${vt}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCategories(res.data.categories || []);
      } catch (err) {
        console.error('Failed to fetch inspection categories', err);
      } finally {
        setLoadingCats(false);
      }
    };
    fetchCats();
  }, [token, vehicleType]);

  const handleStatusChange = (catCode, itemCode, status) => {
    const key = `${catCode}-${itemCode}`;
    const newStatuses = { ...statuses, [key]: status };
    onChange({ ...inspectionData, statuses: newStatuses, measurements: meas, notes });
  };

  const handleMeasurementChange = (catCode, itemCode, value) => {
    const key = `${catCode}-${itemCode}`;
    const newMeas = { ...meas, [key]: value };
    onChange({ ...inspectionData, statuses, measurements: newMeas, notes });
  };

  const handleNotesChange = (newNotes) => {
    onChange({ ...inspectionData, statuses, measurements: meas, notes: newNotes });
  };

  if (loadingCats) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="w-6 h-6 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
        <span className="text-xs text-slate-400 ml-2">Chargement categories inspection...</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
        <SaaqInspectionClassic
          categories={categories}
          itemStatuses={statuses}
          onItemStatusChange={handleStatusChange}
          measurements={meas}
          onMeasurementChange={handleMeasurementChange}
          vehicleType={vehicleType || 'truck'}
          vehicleInfo={vehicleInfo || {}}
          inspectorName={inspectorName || ''}
          inspectionDate={inspectionDate || ''}
          odometer={vehicleInfo?.current_odometer || ''}
          notes={notes}
          onNotesChange={handleNotesChange}
          disabled={disabled}
        />
    </div>
  );
};

const TaskCard = ({ task, index, onUpdate, onRemove, disabled, token, vehicleType, order }) => {
  const [open, setOpen] = useState(true);
  const typeInfo = TASK_TYPES.find(t => t.key === task.type) || LEGACY_TASK_TYPES[task.type] || TASK_TYPES[0];
  const Icon = typeInfo.icon;

  const update = (field, val) => onUpdate(index, { ...task, [field]: val });

  // AxleDiagram helpers for 'mesures' task type
  const measureAxles = task.axles || [
    { axle_number: 1, has_dual_tires: false, left_brake: null, left_tire_inner: null, left_tire_outer: null, right_brake: null, right_tire_inner: null, right_tire_outer: null },
    { axle_number: 2, has_dual_tires: true, left_brake: null, left_tire_inner: null, left_tire_outer: null, right_brake: null, right_tire_inner: null, right_tire_outer: null },
  ];
  const updateAxle = (idx, field, value) => {
    const next = measureAxles.map((a, i) => i === idx ? { ...a, [field]: value } : a);
    update('axles', next);
  };
  const toggleDual = (idx) => {
    const next = measureAxles.map((a, i) => i === idx ? { ...a, has_dual_tires: !a.has_dual_tires, left_tire_outer: !a.has_dual_tires ? a.left_tire_outer : null, right_tire_outer: !a.has_dual_tires ? a.right_tire_outer : null } : a);
    update('axles', next);
  };
  const addAxle = () => {
    const nextNum = measureAxles.length > 0 ? Math.max(...measureAxles.map(a => a.axle_number)) + 1 : 1;
    update('axles', [...measureAxles, { axle_number: nextNum, has_dual_tires: true, left_brake: null, left_tire_inner: null, left_tire_outer: null, right_brake: null, right_tire_inner: null, right_tire_outer: null }]);
  };
  const removeAxle = () => {
    if (measureAxles.length <= 1) return;
    update('axles', measureAxles.slice(0, -1));
  };

  const updatePart = (pi, field, val) => {
    const parts = [...(task.parts || [])];
    parts[pi] = { ...parts[pi], [field]: val };
    update('parts', parts);
  };
  const removePart = (pi) => {
    update('parts', (task.parts || []).filter((_, idx) => idx !== pi));
  };

  const dur = calcDuration(task.start_time, task.end_time);
  const stopwatch = useStopwatch(task.start_time);

  // Start chrono
  const startChrono = () => {
    update('start_time', nowStr());
    stopwatch.setRunning(true);
  };
  // Stop chrono -> sets end_time to now
  const stopChrono = () => {
    update('end_time', nowStr());
    stopwatch.setRunning(false);
  };

  // Auto-detect if chrono should be running (start set, no end)
  useEffect(() => {
    if (task.start_time && !task.end_time && !disabled) {
      stopwatch.setRunning(true);
    }
  }, [task.start_time, task.end_time, disabled]);

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden" data-testid={`task-card-${index}`}>
      <div onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 p-3 text-left cursor-pointer" role="button" tabIndex={0}>
        <div className={`p-1.5 rounded-lg ${typeInfo.color}`}><Icon size={14} /></div>
        <span className="text-sm font-medium text-white flex-1">{typeInfo.label}</span>
        {dur && <span className="text-[10px] text-amber-400">{dur}</span>}
        {!disabled && <span onClick={e => { e.stopPropagation(); onRemove(index); }} className="text-red-400 p-1 cursor-pointer" role="button" tabIndex={0}><X size={14} /></span>}
        {open ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
      </div>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          {/* Description */}
          <Field label="Description" value={task.description} onChange={v => update('description', v)} rows={2} placeholder="Decrire la tache..." disabled={disabled} />

          {/* Type-specific fields */}
          {task.type === 'inspection' && (
            <SaaqInspectionTask
              data={task.inspection_data}
              onChange={v => update('inspection_data', v)}
              disabled={disabled}
              token={token}
              vehicleType={vehicleType}
              vehicleInfo={order ? {
                unit_number: order.vehicle_unit,
                plate_number: order.vehicle_plate,
                brand: order.vehicle_brand,
                model: order.vehicle_model,
                year: order.vehicle_year,
                vin: order.vehicle_vin,
                current_odometer: order.vehicle_odometer,
              } : {}}
              inspectorName={order?.assigned_to_name || order?.mechanic_name || ''}
              inspectionDate={new Date().toISOString().split('T')[0]}
            />
          )}
          {(task.type === 'mesures' || task.type === 'mesures_pneus' || task.type === 'mesures_freins') && (
            <div className="space-y-2">
                <AxleDiagram
                  axles={measureAxles}
                  brakeUnit={task.brake_unit || 'mm'}
                  tireUnit={task.tire_unit || 'mm'}
                  onUpdateAxle={updateAxle}
                  onToggleDual={toggleDual}
                  onAddAxle={addAxle}
                  onRemoveAxle={removeAxle}
                  onBrakeUnitChange={v => update('brake_unit', v)}
                  onTireUnitChange={v => update('tire_unit', v)}
                  disabled={disabled}
                  dark={true}
                  compact={true}
                />
            </div>
          )}
          {task.type === 'entretien' && (
            <div className="space-y-2">
              <p className="text-xs text-slate-300 font-medium">Suivi recurrent</p>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Type service" value={task.recurring_service_type} onChange={v => update('recurring_service_type', v)} placeholder="Ex: Huile moteur" disabled={disabled} />
                <Field label="Intervalle (km)" value={task.recurring_interval_km} onChange={v => update('recurring_interval_km', v)} placeholder="25000" disabled={disabled} />
                <Field label="Intervalle (mois)" value={task.recurring_interval_months} onChange={v => update('recurring_interval_months', v)} placeholder="6" disabled={disabled} />
              </div>
            </div>
          )}

          {/* Notes */}
          <Field label="Notes / Observations" value={task.notes} onChange={v => update('notes', v)} rows={2} placeholder="Notes supplementaires..." disabled={disabled} />

          {/* Time for this task */}
          <div>
            <p className="text-xs text-slate-300 font-medium mb-2">Temps</p>

            {/* Chronometre */}
            {!disabled && (
              <div className="flex items-center gap-2 mb-3">
                {!task.start_time ? (
                  <button onClick={startChrono}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-600 active:bg-emerald-500 text-white rounded-xl text-sm font-medium"
                    data-testid={`task-${index}-start-chrono`}>
                    <Play size={14} /> Demarrer le chrono
                  </button>
                ) : !task.end_time ? (
                  <>
                    <div className="flex-1 flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-3 py-2">
                      <Timer size={14} className="text-emerald-400 animate-pulse" />
                      <span className="text-emerald-400 font-mono font-bold text-sm">{stopwatch.elapsed || '00:00:00'}</span>
                      <span className="text-[10px] text-emerald-500/70 ml-auto">En cours...</span>
                    </div>
                    <button onClick={stopChrono}
                      className="px-4 py-2.5 bg-red-600 active:bg-red-500 text-white rounded-xl text-sm font-medium flex items-center gap-1.5"
                      data-testid={`task-${index}-stop-chrono`}>
                      <Square size={12} /> Stop
                    </button>
                  </>
                ) : (
                  <div className="flex-1 flex items-center gap-2 bg-slate-800/60 border border-slate-700 rounded-xl px-3 py-2">
                    <CheckCircle2 size={14} className="text-amber-400" />
                    <span className="text-amber-400 font-bold text-sm">{dur || '?'}</span>
                    <span className="text-[10px] text-slate-500 ml-auto">Termine</span>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] text-slate-500 mb-1">Debut</label>
                <div className="flex gap-1">
                  <input type="datetime-local" value={task.start_time || ''} onChange={e => update('start_time', e.target.value)} disabled={disabled} className={`flex-1 ${inputCls} text-xs`} />
                  {!disabled && !task.start_time && <button onClick={() => update('start_time', nowStr())} className="px-2 bg-slate-700 rounded-lg text-[10px] text-amber-400 whitespace-nowrap">Maint.</button>}
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-slate-500 mb-1">Fin</label>
                <div className="flex gap-1">
                  <input type="datetime-local" value={task.end_time || ''} onChange={e => { update('end_time', e.target.value); stopwatch.setRunning(false); }} disabled={disabled} className={`flex-1 ${inputCls} text-xs`} />
                  {!disabled && task.start_time && !task.end_time && <button onClick={() => { update('end_time', nowStr()); stopwatch.setRunning(false); }} className="px-2 bg-slate-700 rounded-lg text-[10px] text-amber-400 whitespace-nowrap">Maint.</button>}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2" data-testid={`task-${index}-labor`}>
            <label className="text-[10px] text-slate-500 whitespace-nowrap">Heures facturables</label>
            <input type="number" step="0.25" min="0" value={task.labor_hours ?? ''} onChange={e => update('labor_hours', e.target.value === '' ? null : e.target.value)}
              placeholder={chronoHours(task) ? `${chronoHours(task)} h (chrono)` : 'ex. 1.5'} disabled={disabled}
              className={`w-36 ${inputCls} text-xs py-1.5`} data-testid={`task-${index}-labor-hours`} />
            <span className="text-[10px] text-slate-500">{task.labor_hours != null && task.labor_hours !== '' ? 'corrigé' : chronoHours(task) ? 'chrono' : 'à saisir'}</span>
          </div>

          {/* Anciennes pièces saisies dans la tâche (les nouvelles vont dans « Pièces installées ») */}
          {(task.parts || []).length > 0 && <div>
            <p className="text-xs text-slate-300 font-medium mb-2">Pieces</p>
            <div className="space-y-2">
              {(task.parts || []).map((part, pi) => (
                <div key={pi} className="bg-slate-900 rounded-lg p-2 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500 flex items-center gap-1.5">Piece #{pi + 1} <PartSourceBadge source={part.source} /></span>
                    {!disabled && <button onClick={() => removePart(pi)} className="text-red-400 p-0.5"><X size={12} /></button>}
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <input type="text" value={part.name || ''} onChange={e => updatePart(pi, 'name', e.target.value)} placeholder="Nom" disabled={disabled} className={`${inputCls} text-xs py-1.5`} />
                    <input type="text" value={part.part_number || ''} onChange={e => updatePart(pi, 'part_number', e.target.value)} placeholder="No piece" disabled={disabled} className={`${inputCls} text-xs py-1.5`} />
                    <input type="number" value={part.quantity || ''} onChange={e => updatePart(pi, 'quantity', parseInt(e.target.value) || 0)} placeholder="Qte" disabled={disabled} className={`${inputCls} text-xs py-1.5`} />
                    <input type="text" value={part.cost || ''} onChange={e => updatePart(pi, 'cost', e.target.value)} placeholder="Coût $ (prix coûtant)" disabled={disabled} className={`${inputCls} text-xs py-1.5`} />
                  </div>
                </div>
              ))}
            </div>
          </div>}
        </div>
      )}
    </div>
  );
};


// --- Scan Parts Invoice Component ---
const ScanPartsSection = ({ orderId, token, onPartsAdded }) => {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/mechanic/work-orders/${orderId}/scan-invoice`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      if (res.data.success) {
        setResult(res.data);
        if (res.data.parts?.length > 0) {
          onPartsAdded(res.data.parts);
        }
        toast.success(`${res.data.parts_added} piece(s) ajoutee(s) + envoyee(s) a la comptabilite`);
        const inv = res.data.inventory;
        if (inv?.duplicate) toast.warning('Facture déjà reçue au stock : les pièces sont reliées au bon, sans nouvelle entrée ni 2e fiche en comptabilité');
        else if (inv?.to_confirm) toast.info(`${inv.to_confirm} pièce(s) à confirmer dans l'Inventaire`);
      } else {
        toast.error(res.data.error || 'Erreur extraction');
      }
    } catch (err) {
      toast.error('Erreur scan facture');
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Section title="Scanner facture pieces" icon={ScanLine} defaultOpen={false}>
      {result ? (
        <div className="space-y-2">
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 size={14} className="text-emerald-400" />
              <span className="text-sm text-emerald-400 font-medium">{result.parts_added} piece(s) ajoutee(s)</span>
            </div>
            <p className="text-xs text-slate-400">
              Fournisseur: {result.invoice?.supplier || '?'} | Facture: {result.invoice?.invoice_number || '?'} | Total: {result.invoice?.total}$
            </p>
            <p className="text-[10px] text-slate-500 mt-1">Envoyee automatiquement a la comptabilite</p>
          </div>
          <button onClick={() => setResult(null)} className="text-xs text-amber-400 hover:text-amber-300">Scanner une autre facture</button>
        </div>
      ) : (
        <label className="flex items-center justify-center gap-2 py-3 border border-dashed border-amber-500/30 rounded-xl cursor-pointer active:bg-amber-500/5 transition-colors">
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" className="hidden" onChange={handleScan} disabled={scanning} data-testid="scan-invoice-input" />
          {scanning ? (
            <div className="flex items-center gap-2 text-amber-400 text-sm">
              <div className="w-4 h-4 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" /> Analyse de la facture...
            </div>
          ) : (
            <span className="text-sm text-amber-400 flex items-center gap-2">
              <ScanLine size={16} /> Scanner une facture de pieces
            </span>
          )}
        </label>
      )}
    </Section>
  );
};


// --- Photo Section Component ---
const PhotoSection = ({ orderId, token, photos, disabled, isNew, onPhotosChange }) => {
  const [uploading, setUploading] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(null);
  const fileRef = useRef(null);

  // Build photo URL via backend proxy
  const getPhotoUrl = (photo) => `${API_URL}/api/mechanic/work-orders/${orderId}/photos/${photo.id}/file?token=${token}`;

  const uploadPhoto = async (e) => {
    const files = e.target.files;
    if (!files?.length || isNew) return;
    setUploading(true);
    const newPhotos = [...photos];
    for (let i = 0; i < files.length; i++) {
      try {
        const formData = new FormData();
        formData.append('file', files[i]);
        const res = await axios.post(`${API_URL}/api/mechanic/work-orders/${orderId}/photos`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        if (res.data.success) {
          newPhotos.push(res.data.photo);
          toast.success(`Photo ajoutee`);
        }
      } catch (err) {
        toast.error(`Erreur upload: ${files[i].name}`);
      }
    }
    onPhotosChange(newPhotos);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const deletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API_URL}/api/mechanic/work-orders/${orderId}/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onPhotosChange(photos.filter(p => p.id !== photoId));
      toast.success('Photo supprimee');
    } catch { toast.error('Erreur'); }
  };

  return (
    <Section title="Photos" icon={Camera} badge={photos.length > 0 ? `${photos.length}` : null} defaultOpen={photos.length > 0}>
      {/* Photo grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {photos.map(p => (
            <div key={p.id} className="relative group rounded-xl overflow-hidden bg-slate-800 aspect-square">
              <img src={getPhotoUrl(p)} alt={p.filename || 'photo'} className="w-full h-full object-cover cursor-pointer"
                onClick={() => setViewPhoto(p)} loading="lazy" />
              {!disabled && (
                <button onClick={() => deletePhoto(p.id)}
                  className="absolute top-1 right-1 p-1 bg-red-600/80 rounded-full opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity"
                  data-testid={`delete-photo-${p.id}`}>
                  <X size={12} className="text-white" />
                </button>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1.5 py-0.5">
                <span className="text-[9px] text-white truncate block">{p.uploaded_by || ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      {!disabled && !isNew && (
        <label className="flex items-center justify-center gap-2 py-3 border border-dashed border-amber-500/30 rounded-xl cursor-pointer active:bg-amber-500/5 transition-colors">
          <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={uploadPhoto} disabled={uploading} data-testid="photo-upload-input" />
          {uploading ? (
            <div className="flex items-center gap-2 text-amber-400 text-sm">
              <div className="w-4 h-4 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" /> Upload en cours...
            </div>
          ) : (
            <span className="text-sm text-amber-400 flex items-center gap-2">
              <Camera size={16} /> Prendre une photo / ajouter
            </span>
          )}
        </label>
      )}

      {isNew && (
        <p className="text-xs text-slate-500 text-center py-2">Sauvegardez le bon d'abord pour ajouter des photos</p>
      )}

      {/* Full-screen photo viewer */}
      {viewPhoto && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setViewPhoto(null)}>
          <button onClick={() => setViewPhoto(null)} className="absolute top-4 right-4 p-2 bg-slate-800 rounded-full text-white z-10"><X size={20} /></button>
          <img src={getPhotoUrl(viewPhoto)} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </Section>
  );
};


// === MAIN COMPONENT ===

export default function MechanicWorkOrderDetailPage() {
  const { orderId } = useParams();
  const { token, currentWorkspace } = useMechanicAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromInspectionId = searchParams.get('from_inspection');
  const isNew = orderId === 'nouveau';
  const [showCancel, setShowCancel] = useState(false);

  const [order, setOrder] = useState({
    vehicle_type: 'fleet', vehicle_unit: '', vehicle_plate: '', vehicle_model: '', vehicle_brand: '',
    vehicle_year: '', vehicle_vin: '', vehicle_odometer: '',
    client_name: '', client_address: '', client_phone: '', client_email: '',
    tasks: [], notes: '', start_time: '', end_time: '', status: 'open',
  });
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [showTaskPicker, setShowTaskPicker] = useState(false);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [showVehicleHistory, setShowVehicleHistory] = useState(false);

  const fetchOrder = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await axios.get(`${API_URL}/api/mechanic/work-orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } });
      setOrder(res.data);
    } catch { toast.error('Bon introuvable'); navigate('/admin/mecanicien/bons'); }
    finally { setLoading(false); }
  }, [orderId, token, isNew, navigate]);

  const fetchVehicles = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/api/mechanic/vehicles`, { headers: { Authorization: `Bearer ${token}` } }); setVehicles(res.data); } catch {}
  }, [token]);


  useEffect(() => {
    fetchOrder();
    fetchVehicles();
  }, [fetchOrder, fetchVehicles]);

  // Auto-set start_time for new orders
  useEffect(() => {
    if (isNew && !order.start_time) {
      setOrder(prev => ({ ...prev, start_time: nowStr() }));
    }
  }, [isNew, order.start_time]);

  // Pre-fill from driver inspection (?from_inspection={id})
  useEffect(() => {
    if (!isNew || !fromInspectionId || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/mechanic/inspections/${fromInspectionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const insp = res.data || {};
        const items = Array.isArray(insp.items) ? insp.items : [];
        const defects = items.filter((it) => it?.status === 'defect');

        // Try to match a fleet vehicle by plate/unit
        const matched = (vehicles || []).find((v) => {
          if (insp.vehicle_plate && v.plate_number && v.plate_number.toLowerCase() === String(insp.vehicle_plate).toLowerCase()) return true;
          if (insp.vehicle_unit && v.unit_number && v.unit_number.toLowerCase() === String(insp.vehicle_unit).toLowerCase()) return true;
          return false;
        });

        // Build readable defects summary for the task notes
        const defectLines = defects.map((d) => {
          const name = d.item || d.name || d.label || 'Item';
          const cat = d.category ? `[${d.category}] ` : '';
          const note = d.notes || d.note;
          return `- ${cat}${name}${note ? ' : ' + note : ''}`;
        });
        const driverNotes = (insp.notes || '').trim();
        const taskDescription = [
          `Suite a inspection chauffeur du ${insp.date || ''}`.trim(),
          insp.driver_name ? `Signale par: ${insp.driver_name}` : '',
          '',
          'Defaut(s) constate(s):',
          ...defectLines,
          driverNotes ? '\nRemarques du chauffeur:' : '',
          driverNotes,
        ].filter(Boolean).join('\n');

        setOrder((prev) => {
          // Don't overwrite if user already filled something
          if (prev.vehicle_unit || prev.vehicle_plate || (prev.tasks || []).length > 0) return prev;
          const newInspectionTask = {
            id: `task-${Date.now()}`,
            type: 'inspection',
            description: taskDescription,
            inspection_data: { statuses: {}, measurements: {}, notes: driverNotes || '' },
          };
          return {
            ...prev,
            vehicle_id: matched?.id || prev.vehicle_id,
            vehicle_type: matched ? 'fleet' : prev.vehicle_type,
            vehicle_unit: matched?.unit_number || insp.vehicle_unit || prev.vehicle_unit || '',
            vehicle_plate: matched?.plate_number || insp.vehicle_plate || prev.vehicle_plate || '',
            vehicle_model: matched?.model || prev.vehicle_model || '',
            vehicle_brand: matched?.brand || matched?.make || prev.vehicle_brand || '',
            vehicle_year: matched?.year || prev.vehicle_year || '',
            vehicle_vin: matched?.vin || prev.vehicle_vin || '',
            vehicle_odometer: insp.mileage ? String(insp.mileage) : prev.vehicle_odometer || '',
            notes: prev.notes || taskDescription,
            tasks: [...(prev.tasks || []), newInspectionTask],
            from_inspection_id: fromInspectionId,
          };
        });
        toast.success('Donnees de l\'inspection pre-remplies');
      } catch (err) {
        console.error('Failed to prefill from inspection', err);
        toast.error('Impossible de charger l\'inspection');
      }
    })();
    return () => { cancelled = true; };
  }, [isNew, fromInspectionId, token, vehicles]);

  const updateField = (f, v) => setOrder(prev => ({ ...prev, [f]: v }));

  const selectVehicle = (vid) => {
    const v = vehicles.find(x => x.id === vid);
    if (v) setOrder(prev => ({
      ...prev, vehicle_id: v.id, vehicle_type: 'fleet',
      vehicle_unit: v.unit_number || '', vehicle_plate: v.plate_number || '',
      vehicle_model: v.model || '', vehicle_brand: v.brand || v.make || '',
      vehicle_year: v.year || '', vehicle_vin: v.vin || '',
    }));
  };



  // Task management
  const addTask = (type) => {
    const mkAxle = (n, dual) => ({ axle_number: n, has_dual_tires: dual, left_brake: null, left_tire_inner: null, left_tire_outer: null, right_brake: null, right_tire_inner: null, right_tire_outer: null });
    const newTask = {
      id: generateId(),
      type, description: '', notes: '', parts: [], status: 'pending',
      start_time: nowStr(), end_time: '',
      ...(type === 'mesures' ? { axles: [mkAxle(1, false), mkAxle(2, true)], brake_unit: 'mm', tire_unit: 'mm' } : {}),
      ...(type === 'entretien' ? { recurring_service_type: '', recurring_interval_km: '', recurring_interval_months: '' } : {}),
      ...(type === 'inspection' ? { inspection_data: { statuses: {}, measurements: {} } } : {}),
    };
    setOrder(prev => ({ ...prev, tasks: [...(prev.tasks || []), newTask] }));
    setShowTaskPicker(false);
  };

  const updateTask = (idx, updated) => {
    setOrder(prev => {
      const tasks = [...(prev.tasks || [])];
      tasks[idx] = updated;
      return { ...prev, tasks };
    });
  };

  const removeTask = (idx) => {
    setOrder(prev => ({ ...prev, tasks: (prev.tasks || []).filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const res = await axios.post(`${API_URL}/api/mechanic/work-orders`, {
          vehicle_type: order.vehicle_type,
          vehicle_id: order.vehicle_id || null,
          vehicle_unit: order.vehicle_unit, vehicle_plate: order.vehicle_plate, vehicle_model: order.vehicle_model,
          vehicle_brand: order.vehicle_brand, vehicle_year: order.vehicle_year, vehicle_vin: order.vehicle_vin,
          vehicle_odometer: order.vehicle_odometer,
          client_id: order.client_id || null,
          client_name: order.client_name, client_address: order.client_address, client_phone: order.client_phone,
          client_email: order.client_email, tasks: order.tasks, notes: order.notes,
          workspace_org_id: currentWorkspace?.id || null,
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Bon ${res.data.order_number} cree`);
        navigate(`/admin/mecanicien/bons/${res.data.id}`, { replace: true });
      } else {
        await axios.patch(`${API_URL}/api/mechanic/work-orders/${orderId}`, {
          ...order, start_time: order.start_time || null, end_time: order.end_time || null,
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Sauvegarde');
        fetchOrder();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/api/mechanic/work-orders/${orderId}`, {
        ...order, start_time: order.start_time || null,
        status: 'completed', end_time: order.end_time || nowStr(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      const inv = res.data?.auto_invoice;
      if (inv?.state === 'created' || inv?.state === 'refreshed') {
        toast.success(`Bon complété — facture brouillon ${inv.invoice_number} (${Number(inv.total || 0).toFixed(2)} $) envoyée à l'admin`);
        if (inv.warnings?.length) toast.warning(`À compléter sur la facture : ${inv.warnings.join(' · ')}`);
      } else if (inv?.state === 'locked') {
        toast.success('Bon complété — la facture existante est déjà finalisée');
      } else if (inv?.state === 'kept') {
        toast.success('Bon complété — la facture brouillon modifiée par l\u2019admin est conservée');
      } else {
        toast.success('Bon marque complete');
      }
      fetchOrder();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleCancelled = (updated) => { setShowCancel(false); setOrder(updated); };

  const handleClaim = async () => {
    try {
      await axios.post(`${API_URL}/api/mechanic/work-orders/${orderId}/claim`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Bon pris en charge');
      fetchOrder();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const openPrint = (docType) => {
    window.open(`${API_URL}/api/mechanic/work-orders/${orderId}/print/${docType}?token=${token}`, '_blank');
    setShowPrintMenu(false);
  };

  const handleReopen = async () => {
    if (!window.confirm('Rouvrir ce bon pour correction?')) return;
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/mechanic/work-orders/${orderId}/reopen`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Bon rouvert pour correction');
      fetchOrder();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="w-10 h-10 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" /></div>;

  const isCancelled = order.status === 'cancelled';
  const isCompleted = order.status === 'completed' || isCancelled;

  return (
    <div className="p-4 pb-36 space-y-4" data-testid="mechanic-order-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/mecanicien/bons')} className="p-2 rounded-xl bg-slate-800 text-slate-400" data-testid="back-btn"><ArrowLeft size={20} /></button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-white truncate">{isNew ? 'Nouveau bon' : order.order_number}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            {!isNew && <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[order.status]}`}>{STATUS_LABELS[order.status] || order.status}</span>}
            {order.start_time && <span className="text-[10px] text-slate-500">Ouvert: {order.start_time.replace('T', ' ')}</span>}
          </div>
        </div>
      </div>

      {isCancelled && <CancelledBanner order={order} />}

      {/* Claim unassigned */}
      {!isNew && !isCancelled && !order.mechanic_id && (
        <button onClick={handleClaim} className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 bg-purple-600 active:bg-purple-500 text-white" data-testid="claim-btn">
          Prendre en charge ce bon
        </button>
      )}

      {/* Complete button */}
      {!isNew && !isCompleted && order.mechanic_id && (
        <button onClick={handleComplete} className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 bg-emerald-600 active:bg-emerald-500 text-white" data-testid="complete-btn">
          <CheckCircle2 size={18} /> Marquer complété
        </button>
      )}

      {/* === VEHICLE SECTION === */}
      <Section title="Vehicule" icon={Truck} badge={order.vehicle_plate || null}>
        <div className="flex gap-2 mb-3">
          {['fleet', 'external'].map(vt => (
            <button key={vt} onClick={() => !isCompleted && updateField('vehicle_type', vt)}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${order.vehicle_type === vt ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-400'}`}
              data-testid={`vtype-${vt}`}>
              {vt === 'fleet' ? 'Flotte interne' : 'Externe (walk-in)'}
            </button>
          ))}
        </div>
        {order.vehicle_type === 'fleet' && vehicles.length > 0 && (
          <select onChange={e => selectVehicle(e.target.value)} value={order.vehicle_id || ''} className={selectCls} data-testid="vehicle-select" disabled={isCompleted}>
            <option value="">-- Choisir un vehicule --</option>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.unit_number ? `#${v.unit_number} - ` : ''}{v.plate_number} {v.brand} {v.model}</option>)}
          </select>
        )}
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="No unite" value={order.vehicle_unit} onChange={v => updateField('vehicle_unit', v)} disabled={isCompleted} />
          <Field label="Plaque" value={order.vehicle_plate} onChange={v => updateField('vehicle_plate', v)} disabled={isCompleted} />
          <Field label="Marque" value={order.vehicle_brand} onChange={v => updateField('vehicle_brand', v)} disabled={isCompleted} />
          <Field label="Modele" value={order.vehicle_model} onChange={v => updateField('vehicle_model', v)} disabled={isCompleted} />
          <Field label="Annee" value={order.vehicle_year} onChange={v => updateField('vehicle_year', v)} disabled={isCompleted} />
          <Field label="VIN" value={order.vehicle_vin} onChange={v => updateField('vehicle_vin', v)} disabled={isCompleted} />
        </div>
        <Field label="Odometre (km)" value={order.vehicle_odometer} onChange={v => updateField('vehicle_odometer', v)} disabled={isCompleted} className="mt-2" />
        {order.vehicle_id && (
          <button
            onClick={() => setShowVehicleHistory(true)}
            className="w-full mt-2 py-2.5 border border-amber-500/30 rounded-xl text-sm text-amber-400 flex items-center justify-center gap-2 bg-amber-500/5 active:bg-amber-500/10"
            data-testid="view-vehicle-history-btn"
          >
            <History size={16} /> Consulter l'historique du vehicule
          </button>
        )}
      </Section>

      {/* === CLIENT SECTION === */}
      <Section title="Client" icon={User} defaultOpen={isNew || !!order.client_name}>
        <WorkOrderClientPicker order={order} setOrder={setOrder} token={token} disabled={isCompleted} selectCls={selectCls} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom" value={order.client_name} onChange={v => setOrder(prev => ({ ...prev, client_name: v, client_id: '' }))} disabled={isCompleted} />
          <Field label="Telephone" value={order.client_phone} onChange={v => updateField('client_phone', v)} type="tel" disabled={isCompleted} />
        </div>
        <Field label="Adresse" value={order.client_address} onChange={v => updateField('client_address', v)} disabled={isCompleted} />
        <Field label="Courriel" value={order.client_email} onChange={v => updateField('client_email', v)} type="email" disabled={isCompleted} />
      </Section>

      {/* === TASKS SECTION === */}
      <Section title="Travaux" icon={Wrench} badge={order.tasks?.length ? `${order.tasks.length}` : null}>
        <div className="space-y-3">
          {(order.tasks || []).map((task, i) => (
            <TaskCard key={task.id || i} task={task} index={i} onUpdate={updateTask} onRemove={removeTask} disabled={isCompleted} token={token} vehicleType={order.vehicle_type === 'fleet' ? 'truck' : 'truck'} order={order} />
          ))}

          {!isCompleted && (
            <>
              {showTaskPicker ? (
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-3">
                  <p className="text-xs text-slate-300 font-medium mb-2">Type de tache:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {TASK_TYPES.map(t => (
                      <button key={t.key} onClick={() => addTask(t.key)}
                        className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-900 border border-slate-700 active:border-amber-500 text-left"
                        data-testid={`add-task-${t.key}`}>
                        <div className={`p-1.5 rounded-lg ${t.color}`}><t.icon size={14} /></div>
                        <span className="text-xs text-white">{t.label}</span>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setShowTaskPicker(false)} className="mt-2 text-xs text-slate-500 w-full text-center">Annuler</button>
                </div>
              ) : (
                <button onClick={() => setShowTaskPicker(true)}
                  className="w-full py-3 border border-dashed border-amber-500/30 rounded-xl text-sm text-amber-400 flex items-center justify-center gap-2"
                  data-testid="add-task-btn">
                  <Plus size={16} /> Ajouter une tache
                </button>
              )}
            </>
          )}
        </div>
      </Section>

      {/* === PIECES INSTALLEES (inventaire) === */}
      <Section title="Pièces installées" icon={Package} defaultOpen badge={order.parts?.length ? `${order.parts.length}` : null}>
        <div className="space-y-3" data-testid="installed-parts-section">
          {!order.parts?.length && <p className="text-xs text-slate-500" data-testid="installed-parts-empty">Aucune pièce installée pour l'instant.</p>}
          <OrderPartsList parts={order.parts} onChange={(parts) => setOrder(prev => ({ ...prev, parts }))} disabled={isCompleted} inputCls={inputCls} />
          {isNew && <p className="text-xs text-slate-500">Enregistrez le bon pour ajouter des pièces.</p>}
          {!isNew && !isCompleted && (
            <InventoryPartPicker token={token} order={order} onParts={(parts) => setOrder(prev => ({ ...prev, parts }))} />
          )}
        </div>
      </Section>

      {/* === SCAN FACTURE PIECES === */}
      {!isNew && !isCompleted && (
        <ScanPartsSection orderId={orderId} token={token} onPartsAdded={(parts) => setOrder(prev => ({...prev, parts: [...(prev.parts || []), ...parts]}))} />
      )}

      {/* === TIME SECTION === */}
      <Section title="Temps" icon={Clock} defaultOpen={!isNew}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Debut du bon</label>
            <input type="datetime-local" value={order.start_time || ''} onChange={e => updateField('start_time', e.target.value)} disabled={isCompleted} className={inputCls} data-testid="start-time" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Fin du bon</label>
            <div className="flex gap-1">
              <input type="datetime-local" value={order.end_time || ''} onChange={e => updateField('end_time', e.target.value)} disabled={isCompleted} className={`flex-1 min-w-0 ${inputCls}`} data-testid="end-time" />
              {!isCompleted && !order.end_time && (
                <button onClick={() => updateField('end_time', nowStr())} className="px-2 bg-slate-700 rounded-xl text-[10px] text-amber-400 whitespace-nowrap">Maint.</button>
              )}
            </div>
          </div>
        </div>
        {calcDuration(order.start_time, order.end_time) && (
          <p className="text-xs text-amber-400 mt-1">Duree totale: {calcDuration(order.start_time, order.end_time)}</p>
        )}
      </Section>

      {/* === NOTES === */}
      <Section title="Notes generales" icon={FileText} defaultOpen={!!order.notes}>
        <Field value={order.notes} onChange={v => updateField('notes', v)} rows={3} placeholder="Notes supplementaires..." disabled={isCompleted} />
      </Section>

      {/* === PHOTOS === */}
      <PhotoSection orderId={orderId} token={token} photos={order.photos || []} disabled={isCompleted} isNew={isNew} onPhotosChange={(photos) => setOrder(prev => ({...prev, photos}))} />


      {/* === PRINT MENU === */}
      {!isNew && showPrintMenu && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end justify-center" onClick={() => setShowPrintMenu(false)}>
          <div className="bg-slate-900 border-t border-slate-700 w-full max-w-lg rounded-t-2xl p-4 space-y-2" onClick={e => e.stopPropagation()}>
            <p className="text-white font-semibold text-sm mb-3">Imprimer</p>
            <button onClick={() => { setShowPrintMenu(false); window.print(); }} className="w-full p-3 bg-amber-500/20 border border-amber-500/30 rounded-xl text-left text-sm text-amber-400 font-medium active:bg-amber-500/30" data-testid="print-screen">Imprimer cette page (tel quel)</button>
            <button onClick={() => openPrint('diagnostic')} className="w-full p-3 bg-slate-800 rounded-xl text-left text-sm text-white active:bg-slate-700" data-testid="print-diagnostic">Rapport Diagnostique (PDF)</button>
            <button onClick={() => openPrint('bon')} className="w-full p-3 bg-slate-800 rounded-xl text-left text-sm text-white active:bg-slate-700" data-testid="print-bon">Bon de Travail (PDF)</button>
            <button onClick={() => openPrint('inspection')} className="w-full p-3 bg-slate-800 rounded-xl text-left text-sm text-white active:bg-slate-700" data-testid="print-inspection">Rapport d'Inspection (PDF)</button>
            <button onClick={() => openPrint('full')} className="w-full p-3 bg-slate-800 rounded-xl text-left text-sm text-white active:bg-slate-700" data-testid="print-full">Dossier Complet (PDF)</button>
            <button onClick={() => setShowPrintMenu(false)} className="w-full p-3 text-slate-500 text-sm text-center mt-1">Annuler</button>
          </div>
        </div>
      )}

      {/* === STICKY SAVE BAR === */}
      <div className="fixed bottom-16 left-0 right-0 bg-slate-950/95 backdrop-blur border-t border-slate-800 p-3 z-40">
        {isCancelled ? (
          <div className="flex gap-2">
            <button onClick={() => setShowPrintMenu(true)} className="flex-1 py-3 bg-slate-800 border border-slate-700 text-white rounded-xl flex items-center justify-center gap-2 text-sm" data-testid="print-btn"><Printer size={16} /> Imprimer (archive)</button>
          </div>
        ) : isCompleted ? (
          <div className="flex gap-2">
            <button onClick={handleReopen} disabled={saving}
              className="flex-1 bg-amber-500 active:bg-amber-400 text-black font-bold py-3 rounded-xl flex items-center justify-center gap-2 text-sm disabled:opacity-50" data-testid="reopen-btn">
              <Wrench size={16} /> Ajouter une correction
            </button>
            <button onClick={() => setShowPrintMenu(true)} className="p-3 bg-slate-800 border border-slate-700 text-white rounded-xl" data-testid="print-btn"><Printer size={18} /></button>
            <button onClick={() => setShowCancel(true)} className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl" title="Annuler le bon" data-testid="cancel-order-btn"><Ban size={18} /></button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving}
              className="flex-1 bg-amber-500 active:bg-amber-400 text-black font-bold py-3 rounded-xl flex items-center justify-center gap-2 text-sm disabled:opacity-50" data-testid="save-btn">
              <Save size={16} /> {saving ? 'Sauvegarde...' : isNew ? 'Creer le bon' : 'Sauvegarder'}
            </button>
            {!isNew && (
              <>
                <button onClick={() => setShowPrintMenu(true)} className="p-3 bg-slate-800 border border-slate-700 text-white rounded-xl" data-testid="print-btn"><Printer size={18} /></button>
                <button onClick={() => setShowCancel(true)} className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl" title="Annuler le bon" data-testid="cancel-order-btn"><Ban size={18} /></button>
              </>
            )}
          </div>
        )}
      </div>

      {showCancel && (
        <CancelWorkOrderDialog orderId={orderId} orderNumber={order.order_number} token={token}
          onClose={() => setShowCancel(false)} onCancelled={handleCancelled} />
      )}

      {/* === VEHICLE HISTORY MODAL === */}
      {showVehicleHistory && order.vehicle_id && (
        <VehicleHistoryModal
          vehicle={{
            id: order.vehicle_id,
            unit_number: order.vehicle_unit,
            plate_number: order.vehicle_plate,
            brand: order.vehicle_brand,
            model: order.vehicle_model,
            year: order.vehicle_year,
            vehicle_type: 'truck',
          }}
          token={token}
          apiPrefix="mechanic/fleet/vehicles"
          onClose={() => setShowVehicleHistory(false)}
        />
      )}
    </div>
  );
}
